import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { DividerModule } from '@openng/optimus-ui/divider';

import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { isInvalid } from '../../../../../modules/shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { VatCodeLineObject } from '@libs/types/src/mado/object-type/organization';
import { accountFrag, vatCodeLineMutationFrag } from './organization-settings-vat-code-line-dialog.query';
import { OrganizationSettingsVatCodeLineDialogStore } from './organization-settings-vat-code-line-dialog.store';
import {
    MadoVatCodeLineEntryTypeEnum,
    madoVatCodeLineEntryTypeOptions,
    MadoVatCodeLineTypeEnum,
    madoVatCodeLineTypeOptions,
} from '@libs/types/src/mado/enum';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { FilterBuilder } from 'packages/client/src/app/modules/content/helper/filter.builder';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';

@Component({
    selector: 'app-organization-settings-vat-code-line-dialog',
    templateUrl: './organization-settings-vat-code-line-dialog.component.html',
    providers: [OrganizationSettingsVatCodeLineDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DividerModule,
        SelectInputComponent,
        NumberInputComponent,
    ],
})
export class OrganizationSettingsVatCodeLineDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationSettingsVatCodeLineDialogStore);

    protected readonly vatCodeId: number = this.dialogConfig.data.vatCodeId;
    protected readonly data = this.dialogConfig.data.vatCodeLine || null;

    protected form = fb.group({
        id: fb.control<number | null>(null),
        type: fb.control<string | null>(null, [Validators.required]),
        entryType: fb.control<string | null>(null, [Validators.required]),
        accountId: fb.control<number | null>(null),
        percentage: fb.control<number | null>(null),
    });

    protected accountOptions = computed(() => this.store.data.getMadoAccounts()?.items || []);

    protected readonly prefix = 'organization-settings.mado.vatCodeLine.';
    protected readonly MadoVatCodeLineTypeEnum = MadoVatCodeLineTypeEnum;

    protected readonly madoVatCodeLineEntryTypeOptions = madoVatCodeLineEntryTypeOptions;
    protected readonly madoVatCodeLineTypeOptions = madoVatCodeLineTypeOptions;

    constructor() {
        super();

        this.patchForm(this.data);

        effect(() => {
            this.loadAccounts();
        });

        this.form.get('type')!.valueChanges.subscribe(value => {
            if (value === MadoVatCodeLineTypeEnum.CALCULATED) {
                this.form.controls['percentage'].setValidators([Validators.required]);
            } else {
                this.form.controls['percentage'].clearValidators();
            }
            this.form.controls['percentage'].updateValueAndValidity();
        });
    }

    override async save() {
        if (isInvalid(this.form)) return;

        const data: {
            type: MadoVatCodeLineTypeEnum,
            entryType: MadoVatCodeLineEntryTypeEnum,
            accountId: number | null,
            percentage: number | null,
        } = {
            type: this.form.value.type! as MadoVatCodeLineTypeEnum,
            entryType: this.form.value.entryType! as MadoVatCodeLineEntryTypeEnum,
            accountId: Number(this.form.value.accountId!),
            percentage: Number(this.form.value.percentage!),
        };

        if (data.type === MadoVatCodeLineTypeEnum.BASE) {
            data.accountId = null;
            data.percentage = null;
        }

        if (this.form.value.id) {
            await this.store.api.updateVatCodeLine(
                {
                    id: this.form.value.id,
                    ...data,
                },
                vatCodeLineMutationFrag,
            );
            contentGlobalSuccess.next('common.updated');
            this.dialogRef.close(true);

        } else {
            await this.store.api.createVatCodeLine(
                {
                    vatCodeId: this.vatCodeId,
                    ...data,
                },
                vatCodeLineMutationFrag,
            );
            contentGlobalSuccess.next('common.created');
            this.dialogRef.close(true);
        }
    }

    private patchForm(item: VatCodeLineObject | null) {
        if (item) {
            this.form.patchValue({
                id: item.id,
                type: item.type,
                entryType: item.entryType,
                percentage: item.percentage,
                accountId: item.accountId,
            });
        }
    }

    async loadAccounts() {
        const organizationId = routeParams.organizationId();
        await this.store.api.getMadoAccounts(FilterBuilder.singlePage({ organizationId }), accountFrag);
    }
}
